import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldAlert, LogOut } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { logAuthEvent } from '../../lib/auditLog'

// Inactivity sign-out for an authenticated shell. After IDLE_MS of no activity a warning
// modal appears and counts down WARN_MS; if the user doesn't act, they're signed out at
// IDLE_MS + WARN_MS total. Any activity (this tab OR another open tab, shared via
// localStorage) resets the clock and lifts the warning, so working in one tab keeps every
// tab alive.
//
// Two surfaces reuse this ONE component (never fork the timing/warning/cross-tab logic):
//   • Tenant staff app — mounted in AppLayout with NO props (defaults below: the tenant
//     auth store + supabase.auth.signOut() + /login).
//   • Super-admin console — mounted in AdminLayout with props overriding the session
//     source, the sign-out action (adminSupabase + adminStore + /admin/login) and the
//     cross-tab key (so the two surfaces don't keep each other alive).
const IDLE_MS = 24 * 60_000  // 24 min quiet → show the warning
const WARN_MS = 60_000       // 1 min countdown → sign out
const TICK_MS = 1_000
const WRITE_THROTTLE_MS = 2_000
const ACTIVITY_KEY = 'palstro:last-activity'  // default (tenant) cross-tab key
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

interface IdleLogoutProps {
  /** Whether this surface's session is active. Defaults to the tenant auth store. */
  isAuthenticated?: boolean
  /** Surface-specific sign-out: clear the session AND navigate to the correct login.
   *  Defaults to the tenant path (supabase.auth.signOut → clearSession → /login). */
  onLogout?: () => void | Promise<void>
  /** Cross-tab activity key. Separate surfaces (tenant vs admin) pass different keys so
   *  activity on one console does not keep the other alive. */
  storageKey?: string
}

export function IdleLogout({
  isAuthenticated: isAuthProp,
  onLogout,
  storageKey = ACTIVITY_KEY,
}: IdleLogoutProps = {}) {
  const navigate = useNavigate()
  const tenantClearSession = useAuthStore((s) => s.clearSession)
  const tenantIsAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const isAuthenticated = isAuthProp ?? tenantIsAuthenticated

  const lastActivityRef = useRef<number>(Date.now())
  const lastWriteRef = useRef<number>(0)
  const loggingOutRef = useRef(false)

  const [warnOpen, setWarnOpen] = useState(false)
  const [remainingSec, setRemainingSec] = useState(Math.ceil(WARN_MS / 1000))

  // Default (tenant) sign-out; a surface can override via onLogout. Kept in a ref so
  // doLogout stays stable (and the activity listeners don't re-bind every render when a
  // caller passes an inline onLogout).
  const defaultLogout = useCallback(async () => {
    // Audit the forced/expiry sign-out BEFORE signOut() (RLS needs the live session).
    // "Session expired" distinguishes it from a user-initiated "Signed out".
    const { tenant, user } = useAuthStore.getState()
    await logAuthEvent(tenant?.id, user?.id, 'logout', 'Session expired')
    try {
      await supabase.auth.signOut()
    } catch {
      // Sign-out network failure shouldn't trap the user — clear locally regardless.
    }
    tenantClearSession()
    navigate('/login', { replace: true })
  }, [tenantClearSession, navigate])

  const logoutActionRef = useRef(onLogout ?? defaultLogout)
  logoutActionRef.current = onLogout ?? defaultLogout

  const doLogout = useCallback(async () => {
    if (loggingOutRef.current) return
    loggingOutRef.current = true
    setWarnOpen(false)
    try {
      await logoutActionRef.current()
    } catch {
      // Already best-effort inside the action; never leave the user stuck.
    }
    toast('Signed out due to inactivity', { icon: '🔒' })
  }, [])

  const resetTimer = useCallback(() => {
    const t = Date.now()
    lastActivityRef.current = t
    lastWriteRef.current = t
    try { localStorage.setItem(storageKey, String(t)) } catch { /* storage may be unavailable */ }
    setWarnOpen(false)
  }, [storageKey])

  useEffect(() => {
    if (!isAuthenticated) return
    loggingOutRef.current = false
    lastActivityRef.current = Date.now()

    // Activity in THIS tab — update the ref (cheap) and broadcast to other tabs
    // (throttled). We don't lift the warning here directly; the tick loop derives the
    // modal purely from elapsed time, so a single mouse move auto-dismisses it.
    const onActivity = () => {
      const t = Date.now()
      lastActivityRef.current = t
      if (t - lastWriteRef.current > WRITE_THROTTLE_MS) {
        lastWriteRef.current = t
        try { localStorage.setItem(storageKey, String(t)) } catch { /* ignore */ }
      }
    }

    // Activity in ANOTHER tab (same surface) — adopt its (newer) timestamp.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey || !e.newValue) return
      const t = Number(e.newValue)
      if (Number.isFinite(t) && t > lastActivityRef.current) {
        lastActivityRef.current = t
      }
    }

    // capture:true so scroll inside an inner content pane (scroll doesn't bubble to
    // window) still counts as activity.
    const opts: AddEventListenerOptions = { passive: true, capture: true }
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, opts)
    }
    window.addEventListener('storage', onStorage)

    const interval = window.setInterval(() => {
      if (loggingOutRef.current) return
      const elapsed = Date.now() - lastActivityRef.current
      if (elapsed >= IDLE_MS + WARN_MS) {
        void doLogout()
        return
      }
      if (elapsed >= IDLE_MS) {
        setRemainingSec(Math.max(0, Math.ceil((IDLE_MS + WARN_MS - elapsed) / 1000)))
        setWarnOpen((open) => (open ? open : true))
      } else {
        setWarnOpen((open) => (open ? false : open))
      }
    }, TICK_MS)

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, opts)
      window.removeEventListener('storage', onStorage)
      window.clearInterval(interval)
    }
  }, [isAuthenticated, doLogout, storageKey])

  if (!warnOpen) return null

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-logout-title"
    >
      <div className="card w-full max-w-sm space-y-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <div className="space-y-1">
          <h2 id="idle-logout-title" className="text-base font-semibold text-gray-900">
            Still there?
          </h2>
          <p className="text-sm text-gray-500">
            You’ve been inactive for a while. For your security, you’ll be signed out in
          </p>
          <p className="text-3xl font-bold tabular-nums text-amber-600">{remainingSec}s</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <button type="button" onClick={resetTimer} className="btn-primary w-full">
            Stay signed in
          </button>
          <button
            type="button"
            onClick={() => void doLogout()}
            className="btn-secondary w-full inline-flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" /> Log out now
          </button>
        </div>
      </div>
    </div>
  )
}
